import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import {
  Pencil,
  Trash2,
  Sparkles,
  Ruler,
  Flame,
  Plus,
  CircleMinus,
  Wheat,
  Thermometer,
  Snowflake,
  Milk,
  Droplets,
  BookmarkPlus,
} from 'lucide-react';
import {
  getModifierGroups,
  createModifierGroup,
  updateModifierGroup,
  deleteModifierGroup,
  getOrgTemplates,
  createOrgTemplate,
  deleteOrgTemplate,
  getInventoryItems,
} from '../services/api';
import MenuNav from '../components/MenuNav';
import HelpIcon from '../components/help/HelpIcon';
import TemplatePicker from '../components/TemplatePicker';
import OptionConsumptionEditor from '../components/OptionConsumptionEditor';
import { MODIFIER_TEMPLATES } from '../data/modifierTemplates';
import { templateToGroup, uniqueGroupName, describeSelection } from '../utils/modifierTemplates';
import {
  ORG_TEMPLATE_KINDS,
  buildModifierGroupTemplatePayload,
  groupFromTemplatePayload,
  describeOrgTemplate,
} from '../utils/orgTemplates';
import { formatCurrency } from '../utils/formatCurrency';
// RES-65: clave estable por opción, para que el editor de consumo no herede el estado de otra
import { optionKey } from '../utils/modifierOptions';
import { storage } from '../utils/storage';
import './ModifiersPage.css';

// Iconos de las plantillas de grupos (NER-4488 fase 2). Mapa explícito para no
// arrastrar todo lucide al bundle.
const TEMPLATE_ICONS = {
  Ruler,
  Flame,
  Plus,
  CircleMinus,
  Wheat,
  Thermometer,
  Snowflake,
  Milk,
  Droplets,
};

function ModifierTemplateIcon({ name }) {
  const Icon = TEMPLATE_ICONS[name] || Sparkles;
  return <Icon size={20} aria-hidden="true" />;
}

function ModifiersPage() {
  const { t } = useTranslation();
  const [modifierGroups, setModifierGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState(storage.get('selectedRestaurantId') || '');
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Error del formulario (nombre duplicado / fallo al guardar). Antes era un
  // alert() nativo que las herramientas de QA no ven y que rompe el patrón de
  // modales del admin.
  const [formError, setFormError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  useEscapeKey(showDeleteConfirm, () => setShowDeleteConfirm(false));

  useEffect(() => {
    setFormError('');
  }, [showForm]);
  const [highlightedGroupId, setHighlightedGroupId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [ownTemplates, setOwnTemplates] = useState([]);
  const [ownLoading, setOwnLoading] = useState(false);
  const [templateNotice, setTemplateNotice] = useState('');
  // groupId que se esta guardando como plantilla: evita el doble clic (QA pasada 3:
  // sin feedback visible se creaban plantillas repetidas).
  const [savingTemplateFor, setSavingTemplateFor] = useState(null);
  const groupRefs = useRef({});

  // Catálogo de insumos para el consumo por opción (B3 / RES-31). Si la organización no tiene
  // inventario habilitado la lista queda vacía y la sub-sección no ofrece nada.
  const [inventoryItems, setInventoryItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getInventoryItems()
      .then((data) => {
        if (!cancelled) setInventoryItems(data.items || []);
      })
      .catch(() => {
        if (!cancelled) setInventoryItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Form State
  const [currentGroup, setCurrentGroup] = useState({
    name: '',
    minSelect: 0,
    maxSelect: 1,
    required: false,
    options: [],
  });

  // Compute duplicate name indices for visual disambiguation
  const duplicateNameMap = useMemo(() => {
    const nameCount = {};
    const nameIndex = {};
    modifierGroups.forEach((g) => {
      nameCount[g.name] = (nameCount[g.name] || 0) + 1;
    });
    const result = {};
    modifierGroups.forEach((g) => {
      if (nameCount[g.name] > 1) {
        nameIndex[g.name] = (nameIndex[g.name] || 0) + 1;
        result[g.groupId] = { index: nameIndex[g.name], total: nameCount[g.name] };
      }
    });
    return result;
  }, [modifierGroups]);

  useEffect(() => {
    const handleRestaurantChange = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    window.addEventListener('storage', handleRestaurantChange);
    return () => {
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
      window.removeEventListener('storage', handleRestaurantChange);
    };
  }, []);

  useEffect(() => {
    if (restaurantId) {
      loadModifierGroups();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const loadModifierGroups = async () => {
    try {
      setLoading(true);
      const data = await getModifierGroups(restaurantId);
      setModifierGroups(data.modifierGroups || []);
    } catch (error) {
      console.error('Error loading modifier groups:', error);
      alert('Error loading modifier groups');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (group) => {
    setCurrentGroup(group);
    setIsEditing(true);
    setShowForm(true);
  };

  const confirmDelete = (group) => {
    setGroupToDelete(group);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;
    try {
      await deleteModifierGroup(restaurantId, groupToDelete.groupId);
      setShowDeleteConfirm(false);
      setGroupToDelete(null);
      loadModifierGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Error deleting group');
    }
  };

  const handleCreateNew = () => {
    setCurrentGroup({
      name: '',
      minSelect: 0,
      maxSelect: 1,
      required: false,
      options: [{ name: '', price: 0 }],
    });
    setIsEditing(false);
    setShowForm(true);
  };

  // Plantillas propias de la organización (NER-4488 fase 3): compartidas por
  // todos los restaurantes de la organización.
  const loadOwnTemplates = useCallback(async () => {
    try {
      setOwnLoading(true);
      const data = await getOrgTemplates(ORG_TEMPLATE_KINDS.MODIFIER_GROUP);
      setOwnTemplates(data.templates || []);
    } catch (error) {
      console.error('Error loading organization templates:', error);
      setOwnTemplates([]);
    } finally {
      setOwnLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOwnTemplates();
  }, [loadOwnTemplates]);

  const flashNotice = (message) => {
    setTemplateNotice(message);
    setTimeout(() => setTemplateNotice(''), 4000);
  };

  const handleSaveGroupAsTemplate = async (group) => {
    if (savingTemplateFor) return;
    setSavingTemplateFor(group.groupId);
    try {
      const result = await createOrgTemplate({
        kind: ORG_TEMPLATE_KINDS.MODIFIER_GROUP,
        name: group.name,
        payload: buildModifierGroupTemplatePayload(group),
      });
      await loadOwnTemplates();
      flashNotice(
        result?.existing
          ? t('templates.own.alreadyExists', { name: group.name })
          : t('templates.own.saved', { name: group.name })
      );
    } catch (error) {
      console.error('Error saving organization template:', error);
      flashNotice(error.response?.data?.error || t('templates.own.saveError'));
    } finally {
      setSavingTemplateFor(null);
    }
  };

  const handleUseOwnTemplate = (templateId) => {
    const tpl = ownTemplates.find((item) => item.templateId === templateId);
    if (!tpl) return;
    const group = groupFromTemplatePayload(tpl.payload);
    setCurrentGroup({
      ...group,
      name: uniqueGroupName(group.name || tpl.name, modifierGroups),
      options: group.options.length > 0 ? group.options : [{ name: '', price: 0 }],
    });
    setIsEditing(false);
    setShowTemplates(false);
    setShowForm(true);
  };

  const handleDeleteOwnTemplate = async (templateId) => {
    try {
      await deleteOrgTemplate(templateId);
      await loadOwnTemplates();
      flashNotice(t('templates.own.deleted'));
    } catch (error) {
      console.error('Error deleting organization template:', error);
      flashNotice(error.response?.data?.error || t('templates.own.saveError'));
    }
  };

  // Plantillas de grupos comunes (NER-4488 fase 2): precargan el form de crear.
  const handleUseTemplate = (templateId) => {
    const group = templateToGroup(templateId, t);
    if (!group) return;
    setCurrentGroup({ ...group, name: uniqueGroupName(group.name, modifierGroups) });
    setIsEditing(false);
    setShowTemplates(false);
    setShowForm(true);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...currentGroup.options];
    newOptions[index][field] = value;
    setCurrentGroup({ ...currentGroup, options: newOptions });
  };

  const addOption = () => {
    setCurrentGroup({
      ...currentGroup,
      options: [...currentGroup.options, { name: '', price: 0 }],
    });
  };

  const removeOption = (index) => {
    const newOptions = currentGroup.options.filter((_, i) => i !== index);
    setCurrentGroup({ ...currentGroup, options: newOptions });
  };

  const isDuplicateName = (name) => {
    const trimmed = name.trim().toLowerCase();
    return modifierGroups.some(
      (g) =>
        g.name.trim().toLowerCase() === trimmed &&
        (!isEditing || g.groupId !== currentGroup.groupId)
    );
  };

  const scrollToGroup = useCallback((groupId) => {
    setHighlightedGroupId(groupId);
    requestAnimationFrame(() => {
      const el = groupRefs.current[groupId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    setTimeout(() => setHighlightedGroupId(null), 2000);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDuplicateName(currentGroup.name)) {
      setFormError(
        t('modifiers.duplicateNameError', 'Ya existe un grupo con ese nombre. Usá un nombre único.')
      );
      return;
    }
    setFormError('');
    try {
      let newGroupId = null;
      if (isEditing) {
        await updateModifierGroup(restaurantId, currentGroup.groupId, currentGroup);
        newGroupId = currentGroup.groupId;
      } else {
        const created = await createModifierGroup(restaurantId, currentGroup);
        newGroupId = created?.groupId || created?.id;
      }
      setShowForm(false);
      const data = await getModifierGroups(restaurantId);
      const groups = data.modifierGroups || [];
      setModifierGroups(groups);
      if (newGroupId) {
        requestAnimationFrame(() => scrollToGroup(newGroupId));
      }
    } catch (error) {
      console.error('Error saving modifier group:', error);
      // El backend manda `message` con la opción y la línea a corregir ("Extra queso", insumo 1:
      // la cantidad debe ser mayor a 0); `error` es el título genérico. Mostrar solo el genérico
      // dejaba al usuario sin saber qué corregir (RES-60).
      const data = error.response?.data;
      setFormError(
        data?.message ||
          data?.error ||
          t('modifiers.saveError', 'No se pudo guardar el grupo. Intentá de nuevo.')
      );
    }
  };

  if (!restaurantId) return <div className="p-4">{t('common.selectRestaurant')}</div>;
  if (loading && !showForm) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="modifiers-page">
      <MenuNav />
      <div className="page-header mb-4">
        <h1>
          {t('modifiers.title', 'Modificadores y Extras')}{' '}
          <HelpIcon topicKey="modifiers.overview" />
        </h1>
        {!showForm && (
          <div className="modifiers-header-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowTemplates((prev) => !prev)}
              aria-expanded={showTemplates || modifierGroups.length === 0}
            >
              <Sparkles size={16} aria-hidden="true" /> {t('templates.useTemplate')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleCreateNew}>
              {t('modifiers.newGroup', 'Nuevo Grupo')}
            </button>
          </div>
        )}
      </div>

      {templateNotice && (
        <div className="modifiers-notice" role="status">
          {templateNotice}
        </div>
      )}

      {!showForm && (showTemplates || modifierGroups.length === 0) && (
        <TemplatePicker
          title={t('templates.modifiers.title')}
          hint={
            modifierGroups.length === 0
              ? t('templates.modifiers.emptyHint')
              : t('templates.modifiers.hint')
          }
          helpTopicKey="modifiers.templates"
          templates={MODIFIER_TEMPLATES.map((tpl) => ({
            id: tpl.id,
            icon: <ModifierTemplateIcon name={tpl.icon} />,
            name: t(`modifierTemplates.${tpl.id}.name`),
            description: t(`modifierTemplates.${tpl.id}.description`),
            chips: [
              describeSelection(tpl, t),
              t('templates.chips.optionsCount', { count: tpl.options.length }),
              ...tpl.options
                .slice(0, 3)
                .map((key) => t(`modifierTemplates.${tpl.id}.options.${key}`)),
            ],
          }))}
          onSelect={handleUseTemplate}
          onClose={modifierGroups.length === 0 ? undefined : () => setShowTemplates(false)}
          ownTemplates={ownTemplates.map((tpl) => ({
            id: tpl.templateId,
            name: tpl.name,
            description: tpl.description,
            chips: describeOrgTemplate(tpl, t),
          }))}
          onSelectOwn={handleUseOwnTemplate}
          onDeleteOwn={handleDeleteOwnTemplate}
          ownLoading={ownLoading}
        />
      )}

      {showForm ? (
        <div className="card p-4">
          <h2>{isEditing ? t('modifiers.editGroup') : t('modifiers.createGroup')}</h2>
          <form onSubmit={handleSubmit}>
            {formError && (
              <div className="modifiers-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="form-group mb-3">
              <label htmlFor="modifier-group-name">{t('common.name')}</label>
              <input
                id="modifier-group-name"
                type="text"
                className="form-control"
                value={currentGroup.name}
                aria-invalid={Boolean(formError)}
                onChange={(e) => {
                  setFormError('');
                  setCurrentGroup({ ...currentGroup, name: e.target.value });
                }}
                required
              />
            </div>
            <div className="form-row mb-3" style={{ display: 'flex', gap: '20px' }}>
              <div className="form-group">
                <label htmlFor="modifier-min-select">
                  {t('modifiers.minSelect', 'Mínimo')}{' '}
                  <HelpIcon topicKey="modifiers.minMaxSelect" />
                </label>
                <input
                  id="modifier-min-select"
                  type="number"
                  className="form-control"
                  value={currentGroup.minSelect}
                  onChange={(e) =>
                    setCurrentGroup({ ...currentGroup, minSelect: parseInt(e.target.value) })
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="modifier-max-select">{t('modifiers.maxSelect', 'Máximo')}</label>
                <input
                  id="modifier-max-select"
                  type="number"
                  className="form-control"
                  value={currentGroup.maxSelect}
                  onChange={(e) =>
                    setCurrentGroup({ ...currentGroup, maxSelect: parseInt(e.target.value) })
                  }
                  min="1"
                />
              </div>
              <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                <label htmlFor="modifier-required">
                  <input
                    id="modifier-required"
                    type="checkbox"
                    checked={currentGroup.required}
                    onChange={(e) =>
                      setCurrentGroup({ ...currentGroup, required: e.target.checked })
                    }
                  />{' '}
                  {t('common.required', 'Obligatorio')}
                </label>{' '}
                <HelpIcon topicKey="modifiers.required" />
              </div>
            </div>

            <h2>
              {t('common.options', 'Opciones')}
              <HelpIcon topicKey="modifiers.optionConsumption" />
            </h2>
            <div className="options-list mb-3">
              <div className="option-row-header">
                <span className="option-header-name">{t('modifiers.columnName', 'Nombre')}</span>
                <span className="option-header-price">
                  {t('modifiers.columnPrice', 'Precio extra')}
                </span>
                <span className="option-header-action" aria-hidden="true"></span>
              </div>
              {currentGroup.options.map((option, index) => (
                <div key={optionKey(option)} className="option-row mb-2">
                  <input
                    type="text"
                    placeholder={t('modifiers.optionNamePlaceholder', 'Nombre opción')}
                    aria-label={t('modifiers.optionNameLabel', {
                      number: index + 1,
                      defaultValue: `Nombre opción ${index + 1}`,
                    })}
                    value={option.name}
                    onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                    required
                  />
                  <input
                    type="number"
                    placeholder={t('modifiers.optionPricePlaceholder', 'Precio Extra')}
                    aria-label={t('modifiers.optionPriceLabel', {
                      number: index + 1,
                      defaultValue: `Precio extra opción ${index + 1}`,
                    })}
                    value={option.price}
                    onChange={(e) => handleOptionChange(index, 'price', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    onClick={() => removeOption(index)}
                  >
                    X
                  </button>
                  <OptionConsumptionEditor
                    consumption={option.consumption}
                    inventoryItems={inventoryItems}
                    optionLabel={option.name || t('modifiers.optionNamePlaceholder')}
                    onChange={(consumption) =>
                      handleOptionChange(index, 'consumption', consumption)
                    }
                  />
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>
                + {t('common.addOption', 'Agregar Opción')}
              </button>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="grid-container">
          {modifierGroups.map((group) => (
            <div
              key={group.groupId}
              ref={(el) => {
                groupRefs.current[group.groupId] = el;
              }}
              className={`card p-3 modifier-card${highlightedGroupId === group.groupId ? ' modifier-card--highlight' : ''}`}
            >
              <div className="card-header-flex">
                <h2>
                  {group.name}
                  {duplicateNameMap[group.groupId] && (
                    <span className="duplicate-index">
                      {' '}
                      ({duplicateNameMap[group.groupId].index})
                    </span>
                  )}
                </h2>
                <div className="modifier-actions">
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleSaveGroupAsTemplate(group)}
                    disabled={savingTemplateFor === group.groupId}
                    aria-busy={savingTemplateFor === group.groupId}
                    title={t('templates.saveAsTemplate')}
                    aria-label={t('templates.saveAsTemplate')}
                  >
                    <BookmarkPlus size={18} />
                  </button>
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleEdit(group)}
                    title={t('common.edit')}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    type="button"
                    className="btn-icon btn-icon--danger"
                    onClick={() => confirmDelete(group)}
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="text-muted">
                {group.required
                  ? t('common.required', 'Obligatorio')
                  : t('common.optional', 'Opcional')}{' '}
                • Min: {group.minSelect} / Max: {group.maxSelect} •{' '}
                {t('modifiers.optionsCount', { count: group.options.length })}
              </p>
              <ul className="options-preview">
                {group.options.slice(0, 3).map((opt, i) => (
                  <li key={i}>
                    {opt.name} {opt.price > 0 && `(+${formatCurrency(opt.price)})`}
                  </li>
                ))}
                {group.options.length > 3 && <li>...</li>}
              </ul>
            </div>
          ))}
        </div>
      )}
      {showDeleteConfirm && groupToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('modifiers.deleteGroup')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('modifiers.deleteGroupConfirmation', { name: groupToDelete.name })}</p>
              <p className="warning-text">{t('modifiers.deleteGroupWarning')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                {t('menu.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModifiersPage;
